import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { NotebookPen, Sparkles, Download, Check, Loader2 } from 'lucide-react';
import AskNaviPanel from './AskNaviPanel';
import { API_BASE } from '../../config/api';

const API = `${API_BASE}/api`;

// End-of-module private reflection notes + Ask NAVI entry point.
export default function ModuleReflectionCard({ programmeId, module, token, color }) {
  const base = `${API}/programmes/${programmeId}/modules/${module.module_id}/my-reflection`;
  const headers = { Authorization: `Bearer ${token}` };
  const [text, setText] = useState('');
  const [prompts, setPrompts] = useState([]);
  const [state, setState] = useState('loading');
  const [naviOpen, setNaviOpen] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    axios.get(base, { headers }).then((r) => { setText(r.data.text || ''); setPrompts(r.data.prompts || []); setState('saved'); }).catch(() => setState('error'));
    return () => clearTimeout(timer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base]);

  const onChange = (value) => {
    setText(value);
    setState('typing');
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      setState('saving');
      axios.put(base, { text: value }, { headers }).then(() => setState('saved')).catch(() => { setState('error'); toast.error('Could not save your reflection'); });
    }, 900);
  };

  const download = async () => {
    try {
      const res = await axios.get(`${base}/pdf`, { headers, responseType: 'blob' });
      const href = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = href;
      a.download = `${(module.title || 'Module').replace(/[^A-Za-z0-9]+/g, '_')}_My_Reflections.pdf`;
      a.click();
      URL.revokeObjectURL(href);
    } catch (e) {
      toast.error('Could not download your reflections');
    }
  };

  if (state === 'error' && !prompts.length) return null;
  return (
    <section className="rounded-2xl border border-dashed border-zinc-700 bg-zinc-900/50 p-5 sm:p-6" data-testid={`module-reflection-${module.module_id}`}>
      <div className="flex flex-wrap items-start gap-4 mb-4">
        <span className="h-11 w-11 shrink-0 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${color}22`, color }}><NotebookPen className="h-5 w-5" /></span>
        <div className="flex-1 min-w-[200px]">
          <p className="text-[11px] uppercase tracking-[0.15em] text-zinc-500">End of {module.title}</p>
          <h3 className="text-lg font-semibold text-white">My reflection</h3>
          <p className="text-sm text-zinc-400">Private notes — only you can see these. They save automatically.</p>
        </div>
        <button type="button" onClick={download} className="flex items-center gap-1.5 px-3 py-2 rounded-full text-sm border border-zinc-700 text-zinc-300 hover:bg-zinc-800 transition-colors" data-testid={`reflection-download-${module.module_id}`}>
          <Download className="h-4 w-4" />PDF
        </button>
      </div>
      {prompts.length > 0 && (
        <ul className="mb-3 space-y-1">
          {prompts.map((p) => <li key={p} className="text-sm text-zinc-400 pl-3 border-l-2" style={{ borderColor: color }}>{p}</li>)}
        </ul>
      )}
      <textarea value={text} onChange={(e) => onChange(e.target.value)} rows={6} maxLength={20000} disabled={state === 'loading'}
        placeholder="Write your thoughts, key takeaways and questions here…"
        className="w-full rounded-xl bg-zinc-950 border border-zinc-800 p-4 text-sm text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:border-teal-500 transition-colors resize-y"
        data-testid={`reflection-input-${module.module_id}`} />
      <div className="flex flex-wrap items-center justify-between gap-3 mt-3">
        <span className="text-xs text-zinc-500 flex items-center gap-1.5" data-testid={`reflection-status-${module.module_id}`}>
          {state === 'saving' || state === 'typing' ? <><Loader2 className="h-3.5 w-3.5 animate-spin" />Saving…</> : state === 'saved' ? <><Check className="h-3.5 w-3.5 text-teal-400" />Saved</> : null}
        </span>
        <div className="flex items-center gap-3 rounded-xl bg-navigate-gold/10 border border-navigate-gold/30 px-3 py-2">
          <p className="text-xs text-zinc-300 max-w-xs">Stuck on something? <span className="text-white">Ask NAVI anything</span> about this module — concepts, examples, or how it applies to your work.</p>
          <button type="button" onClick={() => setNaviOpen(true)} className="shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-full bg-navigate-gold text-black text-sm font-medium hover:bg-navigate-gold-light transition-colors" data-testid={`ask-navi-btn-${module.module_id}`}>
            <Sparkles className="h-4 w-4" />Ask NAVI
          </button>
        </div>
      </div>
      <AskNaviPanel open={naviOpen} onOpenChange={setNaviOpen} programmeId={programmeId} module={module} token={token} />
    </section>
  );
}
